"use client";

import {
  useScroll,
  useTransform,
  motion,
} from "motion/react";
import { useRef } from "react";
import { useMobileDevice } from "@/hooks/use-mobile-device";

interface ParallaxProps {
  children: React.ReactNode;
  offset?: number;
  className?: string;
}

export const Parallax = ({ children, offset = 50, className }: ParallaxProps) => {
  const { isMobile } = useMobileDevice();

  if (isMobile) {
    return <div className={className}>{children}</div>;
  }

  return (
    <DesktopParallax offset={offset} className={className}>
      {children}
    </DesktopParallax>
  );
};

export const ParallaxGroup = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef(null);

  return (
    <div ref={ref} className="relative w-full">
      {children}
    </div>
  );
};

interface ParallaxLayerProps {
  children: React.ReactNode;
  speed?: number;
  className?: string;
  asBackground?: boolean;
}

export const ParallaxLayer = ({ 
  children, 
  speed = 1, 
  className = "",
  asBackground = false 
}: ParallaxLayerProps) => {
  const { isMobile } = useMobileDevice();

  if (isMobile) {
    return (
      <div
        className={`${asBackground ? 'fixed' : 'absolute'} w-full ${className}`}
      >
        {children}
      </div>
    );
  }

  return (
    <DesktopParallaxLayer speed={speed} className={className} asBackground={asBackground}>
      {children}
    </DesktopParallaxLayer>
  );
};

const DesktopParallax = ({
  children,
  offset,
  className,
}: Required<Pick<ParallaxProps, "children" | "offset">> & Pick<ParallaxProps, "className">) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", `${offset}%`]);

  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
};

const DesktopParallaxLayer = ({
  children,
  speed,
  className,
  asBackground,
}: Required<Pick<ParallaxLayerProps, "children">> &
  Required<Pick<ParallaxLayerProps, "speed" | "asBackground">> &
  Pick<ParallaxLayerProps, "className">) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", `${speed * 10}%`]);

  return (
    <div
      ref={ref}
      className={`${asBackground ? 'fixed' : 'absolute'} w-full ${className}`}
    >
      <motion.div style={{ y: asBackground ? 0 : y }}>{children}</motion.div>
    </div>
  );
};
